import { PropsWithChildren, useEffect, useMemo, useState } from 'react';
import { useAuth } from '@/modules/auth/contexts/AuthContext';
import CartContext from '@/modules/store/contexts/CartContext';
import { Cart, CartDetails, CartImpl } from '@/modules/store/models/Cart';
import { CartItem } from '@/modules/store/models/CartItem';
import { DeliveryWindow } from '@/modules/store/models/DeliveryWindow';
import { PickupWindow } from '@/modules/store/models/PickupWindow';
import { Supplier } from '@/modules/store/models/Supplier';
import CartSelectionService from '@/modules/store/services/CartSelectionService';
import CartService from '@/modules/store/services/CartService';
import { LocalStorageCartStorage } from '@/modules/store/services/CartStorage';
import CartValidatorService from '@/modules/store/services/CartValidatorService';
import { arrayEqual } from '@/utils/array';

interface CartState {
  carts: Cart[];
  addedSkuIds: string[];
  currentSkuIds: string[];
  hasLoadedFromLocalStorage: boolean;
  nextCartItemId: number;
}

const initialCartState = {
  carts: [],
  addedSkuIds: [],
  currentSkuIds: [],
  hasLoadedFromLocalStorage: false,
  nextCartItemId: 1,
};

const cartSelectionService = new CartSelectionService();
const cartValidatorService = new CartValidatorService();

const getCartStorageKey = (storeId?: string) => (storeId ? `cart:${storeId}` : 'cart');

const createCartService = (storageKey: string) =>
  new CartService(new LocalStorageCartStorage(storageKey));

export const CartProvider = ({ children }: PropsWithChildren) => {
  const { currentUser } = useAuth();
  const storeId = currentUser().storeId;
  const cartStorageKey = getCartStorageKey(storeId);
  const cartService = useMemo(() => createCartService(cartStorageKey), [cartStorageKey]);

  const [state, setState] = useState<CartState>(initialCartState);

  const addToCart = (
    supplierId: string,
    item: CartItem | CartItem[],
    deliveryWindow?: DeliveryWindow,
    pickupWindow?: PickupWindow,
  ) => {
    setState((prevState) => {
      const prevCarts = prevState.carts.map((c) => CartImpl.fromObject(c));

      const result = cartService.addToCart(
        prevCarts,
        supplierId,
        item,
        deliveryWindow,
        pickupWindow,
      );

      const { carts, currentSkuIds, nextCartItemId } = result;

      const addedSkuIds = Array.from(new Set([...prevState.currentSkuIds, ...currentSkuIds]));

      return {
        ...prevState,
        carts: carts,
        addedSkuIds: addedSkuIds,
        currentSkuIds: currentSkuIds,
        nextCartItemId: nextCartItemId,
      };
    });
  };

  const updateItemFromCart = (supplierId: string, itemId: number, quantity: number) => {
    setState((prevState) => {
      const carts = cartService.updateItemFromCart(prevState.carts, supplierId, itemId, quantity);

      return {
        ...prevState,
        carts: carts,
      };
    });
  };

  const removeItemFromCart = (supplierId: string, itemId: number) => {
    setState((prevState) => {
      const carts = cartService.removeItemFromCart(prevState.carts, supplierId, itemId);

      return {
        ...prevState,
        carts: carts,
      };
    });
  };

  const updateDeliveryWindow = (supplierId: string, deliveryWindow: DeliveryWindow | null) => {
    setState((prevState) => {
      const currentDeliveryWindow = cartService.getDeliveryWindow(prevState.carts, supplierId);

      if (currentDeliveryWindow && deliveryWindow?.equals(currentDeliveryWindow)) {
        return prevState;
      }

      const carts = cartService.updateDeliveryWindow(prevState.carts, supplierId, deliveryWindow);

      return {
        ...prevState,
        carts: carts,
      };
    });
  };

  const updateSelectedCartItems = (items: number[], supplierId: string) => {
    setState((prevState) => {
      const allItems = cartService.getAllItems(prevState.carts);
      const currentItems = cartSelectionService.getSelectedItems(prevState.carts, supplierId);
      const filteredItems = items.filter((item) => allItems.includes(item));

      if (arrayEqual(filteredItems, currentItems)) {
        return prevState;
      }

      return {
        ...prevState,
        carts: cartSelectionService.updateSelectedItems(prevState.carts, supplierId, filteredItems),
      };
    });
  };

  const resetCart = () => {
    setState({
      ...initialCartState,
      hasLoadedFromLocalStorage: true,
    });
    void cartService.removeCart();
  };

  const { carts, addedSkuIds, currentSkuIds, hasLoadedFromLocalStorage } = state;

  useEffect(() => {
    setState((prevState) =>
      prevState.hasLoadedFromLocalStorage
        ? {
            ...initialCartState,
          }
        : prevState,
    );
  }, [cartStorageKey]);

  useEffect(() => {
    const loadCartFromLocalStorage = async () => {
      const carts = await cartService.loadCart();
      const uniqueSkuIds = cartService.getSkuIds(carts);

      setState({
        carts: carts,
        addedSkuIds: uniqueSkuIds,
        currentSkuIds: uniqueSkuIds,
        nextCartItemId: cartService.nextItemId(carts),
        hasLoadedFromLocalStorage: true,
      });
    };

    // Load cart from local storage on a component mount only
    if (!hasLoadedFromLocalStorage) {
      void loadCartFromLocalStorage();
      return;
    }

    void cartService.storeCart(carts);
  }, [cartService, carts, hasLoadedFromLocalStorage]);

  const cartItemCount = state.carts.reduce((totalCount, cart) => totalCount + cart.items.length, 0);

  const getCarts = (suppliers?: Supplier[]): Cart[] => {
    return carts.map((cart) => {
      const supplier = suppliers?.find((s) => s.id === cart.supplierId);
      return CartImpl.fromObject(cart, supplier);
    });
  };

  const getSelectedCartsAndItems = (): Cart[] =>
    cartSelectionService.getSelectedCartsAndItems(carts);

  const removeSelectedCartsAndItems = () => {
    setState((prevState) => {
      const newCarts = cartSelectionService.removeSelectedCartsAndItems(carts);

      return {
        ...prevState,
        carts: newCarts,
      };
    });
  };

  const canCheckout = (carts: CartDetails[]) => cartValidatorService.canCheckout(carts);

  return (
    <CartContext.Provider
      value={{
        getCarts,
        getSelectedCartsAndItems,
        cartItemCount,
        addedSkuIds,
        currentSkuIds,
        addToCart,
        updateItemFromCart,
        removeItemFromCart,
        updateDeliveryWindow,
        updateSelectedCartItems,
        resetCart,
        removeSelectedCartsAndItems,
        canCheckout,
        isInitialized: hasLoadedFromLocalStorage,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export default CartProvider;
